// PULSORA WEB PUSH V1
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported as isWebMessagingSupported, onMessage } from 'firebase/messaging';
import { api } from '../services/api';

const firebaseConfig = {
  apiKey: 'AIzaSyBa2kHfig5XaaQ9Ot2x6RggvWZsGv-Gqkk',
  authDomain: 'pulsora-app.firebaseapp.com',
  projectId: 'pulsora-app',
  storageBucket: 'pulsora-app.firebasestorage.app',
  messagingSenderId: '527315612876',
  appId: '1:527315612876:web:a8dde78856cdd7df4e7e69',
};

const WEB_VAPID_PUBLIC_KEY = 'BL393qFyC4FzPICo1UadXgDO-Cz6RHPIV7bzDAR_Di8pHU9IZN2TxOz9kJOD67XAdng0a8WVckFGyFFq_VS6U3E';
const WEB_PUSH_TOKEN_KEY = 'pulsora_web_push_token';

let listenersInitialized = false;
let webForegroundListenerInitialized = false;
let webPushToken = '';

const registerPushToken = async (token, plataforma = Capacitor.getPlatform()) => {
  if (!token) return null;

  const result = await api.notifications.registerDevice({
    token,
    plataforma,
  });

  console.log(
    'PULSORA_PUSH_REGISTERED:',
    result?.dispositivo_push_id,
    result?.plataforma
  );

  return result;
};

const webEnvironmentAvailable = () => (
  !Capacitor.isNativePlatform()
  && typeof window !== 'undefined'
  && typeof Notification !== 'undefined'
  && typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator
);

const getWebMessagingInstance = async () => {
  if (!webEnvironmentAvailable()) return null;
  try {
    const supported = await isWebMessagingSupported();
    if (!supported) return null;
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    return getMessaging(app);
  } catch (error) {
    console.error('PULSORA_WEB_PUSH_SUPPORT_FAILED:', error?.message || error);
    return null;
  }
};

const ensureWebServiceWorker = async () => {
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return registration;
};

const rememberWebToken = token => {
  webPushToken = String(token || '');
  try {
    if (webPushToken) window.sessionStorage?.setItem(WEB_PUSH_TOKEN_KEY, webPushToken);
  } catch (_) { /* sessionStorage puede estar restringido. */ }
};

const rememberedWebToken = () => {
  if (webPushToken) return webPushToken;
  try { return window.sessionStorage?.getItem(WEB_PUSH_TOKEN_KEY) || ''; } catch (_) { return ''; }
};

const clearRememberedWebToken = () => {
  webPushToken = '';
  try { window.sessionStorage?.removeItem(WEB_PUSH_TOKEN_KEY); } catch (_) { /* noop */ }
};

const initializeWebForegroundListener = messaging => {
  if (webForegroundListenerInitialized || !messaging) return;
  webForegroundListenerInitialized = true;
  onMessage(messaging, payload => {
    console.log('PULSORA_WEB_PUSH_RECEIVED:', payload);
    window.dispatchEvent(new Event('pulsora:notifications-updated'));
    window.dispatchEvent(new Event('pulsora:attention-updated'));
  });
};

const registerCurrentWebToken = async () => {
  if (!webEnvironmentAvailable() || Notification.permission !== 'granted') return null;

  const messaging = await getWebMessagingInstance();
  if (!messaging) return null;

  const serviceWorkerRegistration = await ensureWebServiceWorker();
  const token = await getToken(messaging, {
    vapidKey: WEB_VAPID_PUBLIC_KEY,
    serviceWorkerRegistration,
  });

  if (!token) return null;
  await registerPushToken(token, 'web');
  rememberWebToken(token);
  initializeWebForegroundListener(messaging);
  return token;
};

export const getWebPushStatus = async () => {
  if (!webEnvironmentAvailable()) return { supported: false, permission: 'unsupported' };
  const messaging = await getWebMessagingInstance();
  if (!messaging) return { supported: false, permission: 'unsupported' };
  return { supported: true, permission: Notification.permission };
};

export const initializeWebPushIfGranted = async () => {
  const state = await getWebPushStatus();
  if (!state.supported || state.permission !== 'granted') return null;
  return registerCurrentWebToken();
};

export const enableWebPush = async () => {
  const state = await getWebPushStatus();
  if (!state.supported) throw new Error('Este navegador no soporta Web Push.');

  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    const error = new Error(permission === 'denied'
      ? 'Las notificaciones estan bloqueadas en este navegador.'
      : 'No se concedio permiso para notificaciones.');
    error.code = permission === 'denied' ? 'WEB_PUSH_DENIED' : 'WEB_PUSH_NOT_GRANTED';
    throw error;
  }

  return registerCurrentWebToken();
};

export const deactivateWebPushDevice = async () => {
  if (Capacitor.isNativePlatform()) return null;
  const token = rememberedWebToken();
  if (!token) return null;
  try {
    return await api.notifications.unregisterDevice({ token });
  } finally {
    clearRememberedWebToken();
  }
};

export const initializeAndroidPush = async () => {
  if (!Capacitor.isNativePlatform() || !['android', 'ios'].includes(Capacitor.getPlatform())) {
    return null;
  }

  const supported = await FirebaseMessaging.isSupported();

  if (!supported.isSupported) {
    console.warn('PULSORA_PUSH: Firebase Messaging no soportado.');
    return null;
  }

  let permission = await FirebaseMessaging.checkPermissions();

  if (
    permission.receive === 'prompt' ||
    permission.receive === 'prompt-with-rationale'
  ) {
    permission = await FirebaseMessaging.requestPermissions();
  }

  if (permission.receive !== 'granted') {
    console.warn(
      'PULSORA_PUSH: permiso no concedido:',
      permission.receive
    );
    return null;
  }

  const { token } = await FirebaseMessaging.getToken();

  await registerPushToken(token);

  if (!listenersInitialized) {
    listenersInitialized = true;

    await FirebaseMessaging.addListener('tokenReceived', event => {
      registerPushToken(event.token).catch(error => {
        console.error(
          'PULSORA_PUSH_TOKEN_REFRESH_FAILED:',
          error?.message || error
        );
      });
    });

    await FirebaseMessaging.addListener(
      'notificationReceived',
      event => {
        console.log(
          'PULSORA_PUSH_RECEIVED:',
          event.notification
        );
      }
    );

    await FirebaseMessaging.addListener(
      'notificationActionPerformed',
      event => {
        const notification = event?.notification || {};
        const data = notification?.data || event?.data || {};
        const url = String(data?.url || notification?.url || '').trim();
        if (!url || !url.startsWith('/')) return;
        window.location.assign(url);
      }
    );
  }

  return token;
};
